/** @file readChunks: splits a PNG file into chunks, checking the signature and every CRC. */
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { IMAGE_IO_ERRORS } from '../errors/imageIoErrors';
import { crc32 } from '../zlib/crc32';

const SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];

/** Big-endian unsigned 32-bit at `at`. */
function u32(bytes: Uint8Array, at: number): number {
  return (((bytes[at] ?? 0) << 24) | ((bytes[at + 1] ?? 0) << 16) | ((bytes[at + 2] ?? 0) << 8) | (bytes[at + 3] ?? 0)) >>> 0;
}

/** Every chunk up to and including IEND; `PNG_SIGNATURE`, `PNG_TRUNCATED` (no IEND) and `PNG_CRC` at `/png/<type>`. */
export function readChunks(bytes: Uint8Array): { type: string; data: Uint8Array }[] {
  if (bytes.length < 8 || SIGNATURE.some((b, i) => bytes[i] !== b)) return throwError(IMAGE_IO_ERRORS.PNG_SIGNATURE, {}, { path: '/png' });
  const chunks: { type: string; data: Uint8Array }[] = [];
  let at = 8;
  for (;;) {
    if (at + 8 > bytes.length) return throwError(IMAGE_IO_ERRORS.PNG_TRUNCATED, { offset: at }, { path: '/png' });
    const length = u32(bytes, at);
    const type = String.fromCharCode(...bytes.subarray(at + 4, at + 8));
    const end = at + 8 + length;
    if (end + 4 > bytes.length) return throwError(IMAGE_IO_ERRORS.PNG_TRUNCATED, { offset: at, length }, { path: `/png/${type}` });
    if (crc32(bytes.subarray(at + 4, end)) !== u32(bytes, end)) return throwError(IMAGE_IO_ERRORS.PNG_CRC, { offset: at }, { path: `/png/${type}` });
    chunks.push({ type, data: bytes.subarray(at + 8, end) });
    if (type === 'IEND') return chunks;
    at = end + 4;
  }
}
